import React, { useMemo, useState } from 'react';
import { Select } from 'antd';
import { useGetSelectFieldValues } from '@/shared/api/client/field-configuration-controller';
import type { SelectFieldOption } from '@/shared/api/model';

export interface LoanOfficerSelectProps {
  disabled?: boolean;
  placeholder?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  onBlur?: () => void;
}

/**
 * Loan Officer select component.
 * Fetches loan officers from the 'loanOfficer' provider.
 *
 * Used for parameter type: LOAN_OFFICER_SELECT
 */
const LoanOfficerSelect: React.FC<LoanOfficerSelectProps> = ({
  disabled,
  placeholder,
  ...rest
}) => {
  const [search, setSearch] = useState<string | undefined>(undefined);

  const params = useMemo(() => ({
    search,
    fieldName: 'loanOfficer',
    entityType: 'REPORT',
  }), [search]);

  const { data: options, isLoading, error } = useGetSelectFieldValues('loanOfficer', params as any, {
    query: {
      staleTime: 5 * 60 * 1000,
    },
  });

  const selectOptions = useMemo(() => {
    const list: SelectFieldOption[] = Array.isArray(options) ? options : [];
    return list.map((opt) => ({
      value: opt.value,
      label: opt.label,
      disabled: opt.disabled,
    }));
  }, [options]);

  return (
    <Select
      showSearch
      allowClear
      disabled={disabled}
      loading={isLoading}
      placeholder={placeholder || 'Select loan officer'}
      options={selectOptions}
      filterOption={false}
      onSearch={(q) => setSearch(q)}
      notFoundContent={error ? 'Failed to load loan officers' : isLoading ? 'Loading...' : 'No loan officers'}
      optionFilterProp="label"
      {...rest}
    />
  );
};

export default LoanOfficerSelect;
